const RANGE_SELECTOR = ".FormRange";
const INPUT_SELECTOR = ".FormRange__Input";
const WRAPPER_SELECTOR = ".FormRange__Slider__Wrapper";
const OUTPUT_SELECTOR = ".FormRange__Output";
const THUMB_SIZE = 20;

const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value));

const syncRangeState = (input: HTMLInputElement): void => {
  const range = input.closest<HTMLElement>(RANGE_SELECTOR);
  if (!range) return;

  const min = Number(input.min || 0);
  const max = Number(input.max || 100);
  const value = input.valueAsNumber;
  const span = max - min;

  // Avbryt om range-elementets värden inte går att använda.
  if (![min, max, value].every(Number.isFinite) || span <= 0) return;

  const progress = clamp(((value - min) / span) * 100, 0, 100);
  const step = input.step === "any" ? 10 : Number(input.step) || 1;
  const tickStep = Math.min(100, (step / span) * 100);

  // Uppdaterar CSS-variablerna som styr färg och avstånd mellan tickar.
  range.style.setProperty("--progress", `${progress}%`);
  range.style.setProperty("--tick-step", `${tickStep}%`);

  const output = range.querySelector<HTMLOutputElement>(OUTPUT_SELECTOR);
  if (output) {
    output.value = String(value);
    output.dataset.max = String(max);
  }
};

const syncRangeGeometry = (input: HTMLInputElement): void => {
  const wrapper = input.closest<HTMLElement>(WRAPPER_SELECTOR);
  if (!wrapper) return;

  // Linjen börjar och slutar där thumbens centrum kan röra sig.
  const trackLeft = input.offsetLeft + THUMB_SIZE / 2;
  const trackWidth = Math.max(0, input.offsetWidth - THUMB_SIZE);

  wrapper.style.setProperty("--track-left", `${trackLeft}px`);
  wrapper.style.setProperty("--track-width", `${trackWidth}px`);
};

const initFormRangeInputs = (): void => {
  document.querySelectorAll<HTMLInputElement>(INPUT_SELECTOR).forEach((input) => {
    if (input.dataset.customRangeReady === "true") return;

    // Tar bort webbläsarens egna datalist-markeringar.
    input.removeAttribute("list");

    // Ogiltigt/överflödigt ARIA-attribut som inte används.
    input.removeAttribute("aria-valuepredefined");

    const updateAll = (): void => {
      syncRangeState(input);
      syncRangeGeometry(input);
    };

    updateAll();

    // Uppdaterar färg och visat värde vid dragning eller tangentbordsstyrning.
    input.addEventListener("input", () => syncRangeState(input));

    // Väntar tills formulärets återställda värde har tillämpats.
    input.form?.addEventListener("reset", () => {
      requestAnimationFrame(updateAll);
    });

    // Räknar om linjens bredd när input eller dess container ändrar storlek.
    const observer = new ResizeObserver(() => syncRangeGeometry(input));
    observer.observe(input);

    const wrapper = input.closest<HTMLElement>(WRAPPER_SELECTOR);
    if (wrapper) observer.observe(wrapper);

    input.dataset.customRangeReady = "true";
  });
};

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initFormRangeInputs);
} else {
  initFormRangeInputs();
}
